import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";
import { WorkflowVersionFragment } from "queries/workflow-version";

// Two fragments, not one: a listing renders four columns and a workflow's
// `steps` is unbounded. Pulling every step declaration to draw a table of
// names is the kind of thing that is invisible until an organization has
// fifty of them.
export const WorkflowListingFragment = gql`
  fragment WorkflowListingFragment on Workflow {
    id
    workflowId
    title
    tags
    createdAt
    updatedAt
  }
`;

export const WorkflowFragment = gql`
  fragment WorkflowFragment on Workflow {
    ...WorkflowListingFragment

    description
    steps
    subforms
    status
    shareToken

    # The draft above against what the link serves: which version, whether
    # the draft differs from it, and the version itself so the screen can show
    # both without a second query.
    publishedVersion
    changed
    published {
      ...WorkflowVersionFragment
    }
  }
  ${WorkflowListingFragment}
  ${WorkflowVersionFragment}
`;

export const get = gql`
  query WorkflowGet($workflowId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        workflows {
          id
          get(workflowId: $workflowId) {
            ...WorkflowFragment
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${WorkflowFragment}
`;

export const list = gql`
  query WorkflowList($input: ListInput) {
    viewer {
      id
      account {
        id
        workflows {
          id
          list(input: $input) {
            id
            last
            items {
              ...WorkflowListingFragment
            }
          }
        }
      }
    }
  }
  ${WorkflowListingFragment}
`;

export const create = gql`
  mutation WorkflowNew($input: WorkflowInputCreate!) {
    viewer {
      account {
        workflows {
          create(input: $input) {
            ...WorkflowFragment
          }
        }
      }
    }
  }
  ${WorkflowFragment}
`;

export const edit = gql`
  mutation WorkflowEdit($workflowId: String!, $input: WorkflowInputUpdate!) {
    viewer {
      account {
        workflows {
          get(workflowId: $workflowId) {
            update(input: $input) {
              ...WorkflowFragment
            }
          }
        }
      }
    }
  }
  ${WorkflowFragment}
`;

// Enable (or regenerate — same call) and disable the share link. Answers the
// workflow, so the cache takes the new token under the same id.
export const share = gql`
  mutation WorkflowShare($workflowId: String!, $enabled: Boolean!) {
    viewer {
      account {
        workflows {
          get(workflowId: $workflowId) {
            share(enabled: $enabled) {
              ...WorkflowFragment
            }
          }
        }
      }
    }
  }
  ${WorkflowFragment}
`;

// Freeze the draft as the next version. Answers the workflow, so the cache
// takes `publishedVersion`, `published` and `changed: false` under the same
// id; the versions listing is evicted by the caller, since a new row is a
// page it never held.
export const publish = gql`
  mutation WorkflowPublish($workflowId: String!) {
    viewer {
      account {
        workflows {
          get(workflowId: $workflowId) {
            publish {
              ...WorkflowFragment
            }
          }
        }
      }
    }
  }
  ${WorkflowFragment}
`;

export const remove = gql`
  mutation WorkflowRemove($workflowId: String!) {
    viewer {
      account {
        workflows {
          get(workflowId: $workflowId) {
            remove
          }
        }
      }
    }
  }
`;

// The literal below must be a substring of what WorkflowQueries.id resolves to
// on the API side (api/code/src/schema/account/workflow). If the two drift, a
// create succeeds and the list screen stays stale with no error anywhere —
// which is why api/code/test/schema.test.js pins the other half.
export const update = ({ workflowId }) => (cache) => {

  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`workflows/queries`)) {
      cache.data.delete(key);
    }
  });
};
